<template>
	<div>
		<div class="d-grid d-sm-flex justify-content-sm-between gap-2 mb-2">
			<router-link :to="{path: '/admin/exams'}" v-if="exam && !exam.is_archived" class="btn btn-outline-secondary">{{$t('btn.goto-exams')}}</router-link>
			<form role="form" style="min-width: 50%;" @submit.prevent="page = 1; loadData();">
				<div class="input-group">
					<input type="text" v-model="search" class="form-control" :placeholder="$t('placeholder.search')" />
					<button type="submit" class="btn btn-outline-secondary" :disabled="loading" :content="$t('tooltip.btn.search')" v-tippy="{placement: 'top'}">
						<font-awesome-icon :icon="['fas', loading ? 'spinner' : 'magnifying-glass']" :spin="loading" />
					</button>
				</div>
			</form>
			<router-link :to="{path: '/admin/exams-archive'}" v-if="exam && exam.is_archived" class="btn btn-outline-secondary">{{$t('btn.goto-exams-archive')}}</router-link>
			<button type="button" class="btn btn-outline-primary" @click="onShowAddModal()" v-if="exam && !exam.is_archived">{{$t('btn.add-students')}}</button>
		</div>
		<div class="card mb-2" v-if="exam">
			<div class="card-body">
				<div class="row">
					<div class="col-md-2 text-md-end"><b>{{$t('text.group')}}:</b></div>
					<div class="col-md-7">{{exam.group_name}}</div>
					<div class="col-md-1 text-md-end"><b>{{$t('text.lang')}}:</b></div>
					<div class="col-md-2">{{exam.lang.toUpperCase()}}</div>
				</div>
				<div class="row">
					<div class="col-md-2 text-md-end"><b>{{$t('text.organization-cut')}}:</b></div>
					<div class="col-md-7">{{exam.organization_shortname}}</div>
					<div class="col-md-1 text-md-end"><b>{{$t('text.date')}}:</b></div>
					<div class="col-md-2">{{formatDate(exam.date, 'DD.MM.YYYY')}}</div>
				</div>
			</div>
		</div>
		<div class="table-responsive">
			<table class="table table-hover align-middle">
				<thead>
					<tr>
						<th scope="col">{{$t('text.student-fio')}}</th>
						<th scope="col" class="text-center" width="150">{{$t('text.status')}}</th>
						<th scope="col" class="text-center" width="100">{{$t('text.date-start')}}</th>
						<th scope="col" class="text-center" width="100">{{$t('text.date-finish')}}</th>
						<th scope="col" class="text-center" width="130">{{$t('text.duration')}}</th>
						<th scope="col" class="text-end" width="200">
							<font-awesome-icon :icon="['fas', 'spinner']" spin v-if="loading" />
						</th>
					</tr>
				</thead>
				<tbody>
					<tr v-for="student in students" :key="student.id">
						<td>{{student.fio}}</td>
						<td class="text-center" :class="{
							'text-warning': student.status == 'started',
							'text-success': student.status == 'ended'
						}">{{$t('exams.students.statuses.'+student.status)}}</td>
						<td class="text-center">{{student.date_start ? formatDate(student.date_start, 'HH:mm:ss') : '-'}}</td>
						<td class="text-center">{{student.date_finish ? formatDate(student.date_finish, 'HH:mm:ss') : '-'}}</td>
						<td class="text-center" :class="{
							'text-primary': student.status != 'ended'
						}">
							<div v-if="student.status == 'waiting'">0</div>
							<div v-else-if="student.status == 'ended'">
								{{getDateDuration(student.date_start, student.date_finish)}}
							</div>
							<div v-else-if="student.status == 'started'">
								{{getDateDuration(student.date_start)}}
							</div>
							<div>{{$t('text.mins')}}</div>
						</td>
						<td>
							<div class="btn-toolbar justify-content-end gap-2">
								<a :href="'/api/pdf/report-student-answers/'+$route.params.exam_id+'/'+student.id+'?lang='+appModel.getLang()" target="_blank" class="btn btn-outline-secondary" v-if="student.status == 'ended'" :content="$t('tooltip.btn.answers')" v-tippy="{placement: 'top'}">{{$t('btn.answers')}}</a>
								<div class="btn-group" v-if="['waiting', 'ended'].includes(student.status)">
									<button type="button" class="btn btn-outline-primary" @click="onShowEditModal(student.id)" :content="$t('tooltip.btn.change')" v-tippy="{placement: 'top'}" v-if="student.status == 'waiting'">
										<font-awesome-icon :icon="['fas', 'pencil']" />
									</button>
									<button type="button" class="btn btn-outline-danger" @click="onShowDeleteModal(student.id)" :content="$t('tooltip.btn.delete')" v-tippy="{placement: 'top'}">
										<font-awesome-icon :icon="['fas', 'trash-can']" />
									</button>
								</div>
							</div>
						</td>
					</tr>
				</tbody>
			</table>
		</div>
		<div class="text-center mt-2">
			<div class="mb-1">{{$t('text.total-exam-students')}}: {{students_count}}</div>
			<nav aria-label="Page navigation">
				<ul class="pagination justify-content-center mb-0">
					<li class="page-item" :class="{'disabled': page == 1}">
						<a class="page-link" href="javascript://" @click="gotoPage(page - 1)" aria-label="Previous">
							<font-awesome-icon :icon="['fas', 'chevron-left']" />
						</a>
					</li>
					<li class="page-item" :class="{'active': page == 1}">
						<a class="page-link" href="javascript://" @click="gotoPage(1)">1</a>
					</li>
					<li class="page-item disabled" v-if="page - paginationAlpha > 2">
						<span class="page-link">...</span>
					</li>
					<li class="page-item" :class="{'active': page == i}" v-for="i in range(paginationA1, paginationA2)" :key="i" v-show="paginationA2 >= paginationA1">
						<a class="page-link" href="javascript://" @click="gotoPage(i)">{{i}}</a>
					</li>
					<li class="page-item disabled" v-if="page + paginationAlpha < getPagesCount() - 1">
						<span class="page-link">...</span>
					</li>
					<li class="page-item" :class="{'active': page == getPagesCount()}" v-if="getPagesCount() > 1">
						<a class="page-link" href="javascript://" @click="gotoPage(getPagesCount())">{{getPagesCount()}}</a>
					</li>
					<li class="page-item" :class="{'disabled': page == getPagesCount() || getPagesCount() == 0}">
						<a class="page-link" href="javascript://" @click="gotoPage(page + 1)" aria-label="Next">
							<font-awesome-icon :icon="['fas', 'chevron-right']" />
						</a>
					</li>
				</ul>
			</nav>
		</div>
	</div>
</template>

<style lang="scss">
      
</style>

<script>
import lib from '@/lib';
import { useMeta } from 'vue-meta';

import ExamStudentsAddModal from '@/components/modals/examsStudents/ExamStudentsAddModal';
import ExamStudentEditModal from '@/components/modals/examsStudents/ExamStudentEditModal';
import ExamStudentDeleteModal from '@/components/modals/examsStudents/ExamStudentDeleteModal';

export default {
	mixins: lib.mixins,
	setup(){
		useMeta({title: 'Студенты экзамена | KMIB EDU Admin'});
	},
	data: () => ({
		exam: null,
		
		students: [],
		students_count: 0,
		
		search: '',
		page: 1,
		limit: 25,
		loading: true,
		error: '',
		
		paginationAlpha: 3,
	}),
	computed: {
		paginationA1(){
			return Math.max(this.page - this.paginationAlpha, 2);
		},
		paginationA2(){
			return Math.min(this.page + this.paginationAlpha, this.getPagesCount() - 1);
		},
	},
	methods: {
		range: lib.range,
		formatDate: lib.formatDate,
		
		onShowAddModal(){
			let that = this;
			this.$vfm.show({
				component: ExamStudentsAddModal,
				bind: {
					styles: {
						display: 'block',
					},
				},
				on: {
					add(close){
						that.loadData();
						close();
					},
					cancel(close){
						close();
					},
				},
				params: {
					exam_id: this.$route.params.exam_id,
					group_id: this.exam.group_id,
				},
			});
		},
		onShowEditModal(id){
			let that = this;
			this.$vfm.show({
				component: ExamStudentEditModal,
				bind: {
					styles: {
						display: 'block',
					},
				},
				on: {
					edit(close){
						that.loadData();
						close();
					},
					cancel(close){
						close();
					},
				},
				params: {
					exam_id: this.$route.params.exam_id,
					student: this.students.find((el) => el.id == id),
				},
			});
		},
		onShowDeleteModal(id){
			let that = this;
			this.$vfm.show({
				component: ExamStudentDeleteModal,
				bind: {
					styles: {
						display: 'block',
					},
				},
				on: {
					delete(close){
						that.loadData();
						close();
					},
					cancel(close){
						close();
					},
				},
				params: {
					exam_id: this.$route.params.exam_id,
					student: this.students.find((el) => el.id == id),
				},
			});
		},
		
		async loadExam(){
			let ret = await this.appModel.doGetExam(this.$route.params.exam_id);
			if(ret.success){
				this.exam = ret.item;
			}
		},
		
		async loadData(){
			this.loading = true;
			this.error = '';
			this.$router.push({query: Object.assign({}, this.$route.query, {
				search: this.search,
				page: this.page,
			})});
			const params = {
				search: this.search,
				start: (this.page - 1) * this.limit,
				limit: this.limit,
			};
			const ret = await this.appModel.doGetExamStudents(this.$route.params.exam_id, params);
			if(ret.success){
				this.students = ret.list;
				this.students_count = ret.count;
			} else {
				this.error = ret.message;
			}
			this.loading = false;
		},
		
		getDateDuration(date_start, date_finish){
			return (date_finish ?
					lib.dateDiff(date_start, date_finish).minutes
				:
					lib.dateDiff(date_start, new Date().toUTCString()).minutes
				);
		},
		
		gotoPage(page){
			if(page >= 1 && page <= this.getPagesCount() && page != this.page){
				this.page = page;
				this.loadData();
				window.scrollTo(0, 0);
			}
		},
		// получение количества страниц
		getPagesCount(){
			return Math.ceil(this.students_count / this.limit);
		},
	},
	async mounted(){
		this.appModel.pageTitle = 'title.exams.students';
		
		if(this.$route.query.search) this.search = this.$route.query.search;
		if(this.$route.query.page) this.page = parseInt(this.$route.query.page || 1);
		
		await this.loadExam();
		await this.loadData();
		
		window.scrollTo(0, 0);
	},
	beforeMount(){
		if(this.authModel.isAuth()){
			if(this.authModel.isEditor()){
				this.$router.push({path: '/editor'});
			} else if(this.authModel.isStudent()){
				this.$router.push({path: '/'});
			}
		}
	},
	beforeRouteUpdate(to, from, next){
		//window.scrollTo(0, 0);
		next();
	},
};
</script>